import React, { useState } from 'react';
import {
  X, User, Lock, Mail, Phone, Eye, EyeOff, ShieldCheck, IdCard, CalendarDays,
  UserRound,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { useToast } from '../hooks/use-toast';
import client from '../lib/api';
import Logo from './Logo';
import { useActiveTheme } from '../lib/activeTheme';
import { HOOKS, hook } from '../lib/domHooks';

// Kayıt ve şifre sıfırlama kuralları backend modelleriyle aynı tutulur.
const REGISTER_PASSWORD_MIN_LENGTH = 6;
const RESET_PASSWORD_MIN_LENGTH = 8;

// Üyelik 18 yaş sınırına tabidir; takvim bu tarihten sonrasını seçtirmez.
const eighteenYearsAgo = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 18);
  return d.toISOString().slice(0, 10);
};

const AuthModal = ({ open, onClose, defaultTab = 'login', registrationOpen = true }) => {
  const maxBirthDate = React.useMemo(eighteenYearsAgo, []);
  // Kural metni yalnızca kullanıcı yazmaya başladıktan sonra uyarıya döner.
  const passwordTooShort = (value, minLength) => value.length > 0 && value.length < minLength;
  const [tab, setTab] = useState(defaultTab);
  const [showPass, setShowPass] = useState(false);
  // Hazır şemalarda pencerenin tepesinde logo yerine altın başlık metni durur.
  const activeTheme = useActiveTheme();
  const themedTitle = ['merit', 'betrun', 'zumrut', 'paris', 'herkul', 'vega'].includes(activeTheme);
  // Herkül şeması pencereyi referans arayüzün biçiminde çizer: başlık
  // çubuğu, altı çizili sekmeler ve ikonsuz düz alanlar. Mantık ortaktır,
  // değişen yalnızca kabuk ve alan biçimidir (bkz. themes/herkul/herkul.css).
  const herkul = activeTheme === 'herkul';
  // Vega da pencereye kendi kabuğunu giydirir (hap sekmeler, 5px köşeli
  // alanlar); alan etiketleri Herkül'e özeldir, burada yer tutucu kalır.
  const vega = activeTheme === 'vega';
  // Referans arayüzde her alanın üstünde bir etiket vardır; diğer
  // şemalar yer tutucuyla yetinir, bu yüzden etiket şemaya bağlıdır.
  const fieldLabel = (text) => (
    herkul ? <label className="herkul-auth-label">{text}</label> : null
  );
  // Betrun'da pencerenin tepesinde başlık metninin **üstünde** marka logosu
  // da durur: kuruluma özel bir istektir (giriş ekranında logo görünsün) ve
  // diğer şemalar referans arayüzlerindeki gibi yalnızca başlık taşımaya
  // devam eder.
  const brandedTitle = activeTheme === 'betrun';
  const { login, register, verifyTwoFactor, resendTwoFactor } = useAuth();
  const publicSettings = useSettings();
  const registrationOtpEnabled = Boolean(
    publicSettings.registration_otp_enabled,
  );
  // Kayıtta otomatik KYC kapalıyken kimlik bilgisi kayıt formunda hiç
  // sorulmaz: üye sonradan Hesabım → KYC Doğrulama sayfasından başvurur ve
  // yönetici onaylar (bkz. backend services/kyc.py).
  const identityAtSignup = publicSettings.kyc_auto_approve !== false;
  const { toast } = useToast();
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [regForm, setRegForm] = useState({
    username: '', password: '', email: '', phone: '',
    first_name: '', last_name: '', tc_no: '', birth_date: '', accept: false,
  });
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [resetConfirm, setResetConfirm] = useState('');
  const [twoFactor, setTwoFactor] = useState(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorBusy, setTwoFactorBusy] = useState(false);
  const [resetToken] = useState(() => {
    const url = new URL(window.location.href);
    const hash = new URLSearchParams(url.hash.replace(/^#/, ''));
    const token = hash.get('reset_token')
      || url.searchParams.get('reset_token')
      || '';
    if (token) {
      hash.delete('reset_token');
      url.searchParams.delete('reset_token');
      const cleanHash = hash.toString();
      window.history.replaceState(
        {},
        '',
        `${url.pathname}${url.search}${cleanHash ? `#${cleanHash}` : ''}`,
      );
    }
    return token;
  });

  React.useEffect(() => {
    setTab(resetToken ? 'reset' : defaultTab === 'register' && !registrationOpen ? 'login' : defaultTab);
    setTwoFactor(null);
    setTwoFactorCode('');
  }, [defaultTab, open, registrationOpen, resetToken]);

  if (!open) return null;

  const submitLogin = async (e) => {
    e.preventDefault();
    const r = await login(loginForm.username, loginForm.password);
    if (r.ok) { toast({ title: 'Hoş geldiniz', description: `${loginForm.username} olarak giriş yapıldı` }); onClose(); return; }
    if (r.twoFactor) {
      setTwoFactor(r.twoFactor);
      setTwoFactorCode('');
      setTab('twofa');
      toast(r.twoFactor.deliveryChannel === 'totp'
        ? { title: 'Doğrulama gerekiyor', description: 'Google Authenticator uygulamanızdaki güncel kodu girin' }
        : { title: 'Doğrulama kodu gönderildi', description: `${r.twoFactor.deliveryTarget} hedefine 6 haneli kod gönderildi` });
      return;
    }
    toast({ title: 'Giriş başarısız', description: r.msg });
  };
  const submitTwoFactor = async (e) => {
    e.preventDefault();
    setTwoFactorBusy(true);
    try {
      const r = await verifyTwoFactor(
        twoFactor.challenge,
        twoFactorCode,
        twoFactor.purpose,
      );
      if (r.ok) {
        toast({
          title: twoFactor.purpose === 'register' ? 'Kayıt tamamlandı' : 'Hoş geldiniz',
          description: twoFactor.purpose === 'register'
            ? 'Telefon numaranız doğrulandı ve hesabınız açıldı.'
            : `${loginForm.username} olarak giriş yapıldı`,
        });
        setTwoFactor(null);
        setTwoFactorCode('');
        onClose();
      } else {
        toast({ title: 'Doğrulama başarısız', description: r.msg });
      }
    } finally {
      setTwoFactorBusy(false);
    }
  };
  const resendCode = async () => {
    setTwoFactorBusy(true);
    try {
      const r = await resendTwoFactor(twoFactor.challenge, twoFactor.purpose);
      toast(r.ok
        ? { title: 'Yeni kod gönderildi', description: `${twoFactor.deliveryTarget} hedefini kontrol edin` }
        : { title: 'Kod gönderilemedi', description: r.msg });
    } finally {
      setTwoFactorBusy(false);
    }
  };
  const cancelTwoFactor = () => {
    setTwoFactor(null);
    setTwoFactorCode('');
    setTab('login');
  };
  const submitRegister = async (e) => {
    e.preventDefault();
    if (!registrationOpen) { toast({ title: 'Kayıtlar kapalı', description: 'Yeni üyelik alımı şu anda kapalıdır.' }); return; }
    if (!regForm.accept) { toast({ title: 'Onay gerekli', description: 'Sözleşmeleri onaylayınız' }); return; }
    const {
      accept, first_name: firstName, last_name: lastName, tc_no: tcNo,
      birth_date: birthDate, ...payload
    } = regForm;
    const values = {
      ...payload,
      username: payload.username.trim(),
      email: payload.email.trim(),
      ...(identityAtSignup ? {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        tc_no: tcNo.trim(),
        birth_date: birthDate,
      } : {}),
    };
    if (identityAtSignup) {
      if (!/^\d{11}$/.test(values.tc_no)) {
        toast({ title: 'Kimlik bilgisi eksik', description: 'T.C. kimlik numarası 11 haneli olmalıdır.' });
        return;
      }
      if (!values.first_name || !values.last_name || !values.birth_date) {
        toast({ title: 'Kimlik bilgisi eksik', description: 'Ad, soyad ve doğum tarihi zorunludur.' });
        return;
      }
    }
    if (values.password.length < REGISTER_PASSWORD_MIN_LENGTH) {
      toast({
        title: 'Şifre çok kısa',
        description: `Şifreniz en az ${REGISTER_PASSWORD_MIN_LENGTH} karakter olmalıdır.`,
      });
      return;
    }
    const r = await register(values);
    if (r.ok) {
      toast({
        title: 'Kayıt başarılı',
        description: identityAtSignup
          ? 'Hesabınız oluşturuldu ve kimlik doğrulamanız tamamlandı.'
          : 'Hesabınız oluşturuldu. Para çekebilmek için Hesabım → KYC '
            + 'Doğrulama sayfasından kimlik bilgilerinizi gönderin.',
      });
      onClose();
    } else if (r.verification) {
      setTwoFactor(r.verification);
      setTwoFactorCode('');
      setTab('twofa');
      toast({
        title: 'Doğrulama kodu gönderildi',
        description: `${r.verification.deliveryTarget} hedefine 6 haneli kod gönderildi`,
      });
    } else toast({ title: 'Kayıt başarısız', description: r.msg });
  };
  const submitForgot = async (e) => {
    e.preventDefault();
    try {
      const { data } = await client.post('/auth/password/forgot', { email: forgotEmail });
      toast({ title: 'Talep alındı', description: data.message });
      setTab('login');
    } catch (error) {
      toast({ title: 'Gönderilemedi', description: error.response?.data?.detail || 'Bir hata oluştu' });
    }
  };
  const submitReset = async (e) => {
    e.preventDefault();
    if (resetPassword.length < RESET_PASSWORD_MIN_LENGTH) {
      toast({
        title: 'Şifre çok kısa',
        description: `Şifreniz en az ${RESET_PASSWORD_MIN_LENGTH} karakter olmalıdır.`,
      });
      return;
    }
    if (resetPassword !== resetConfirm) {
      toast({ title: 'Şifreler eşleşmiyor' });
      return;
    }
    try {
      await client.post('/auth/password/reset', { token: resetToken, password: resetPassword });
      toast({ title: 'Şifreniz güncellendi', description: 'Yeni şifrenizle giriş yapabilirsiniz' });
      setTab('login');
    } catch (error) {
      toast({ title: 'Şifre güncellenemedi', description: error.response?.data?.detail || 'Bağlantı geçersiz' });
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)' }} />
      <div
        {...hook(HOOKS.AUTH_MODAL)}
        className={`responsive-dialog relative w-full max-w-md md:max-w-lg pusu-panel overflow-y-auto${herkul ? ' herkul-auth' : ''}${vega ? ' vega-auth' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 side-icon-btn inline-flex"
          style={{ width: 36, height: 36 }}
          aria-label="Giriş penceresini kapat"
        >
          <X size={16} />
        </button>
        <div className="p-6 md:p-8 auth-shell">
          {/* Hazır şemalarda pencerenin tepesinde logo değil, referans
              arayüzlerdeki gibi altın bir başlık metni durur. */}
          {themedTitle ? (
            <>
            {brandedTitle && (
              <div className="auth-brand-logo" data-bhs="auth-brand">
                <Logo linked={false} size="lg" />
              </div>
            )}
            <h2 id="auth-modal-title" className="mb-5 text-xl font-bold" style={{ color: 'var(--pusu-gold)' }}>
              {tab === 'login' ? (herkul ? 'Giriş Yap' : 'Giriş Yapın') : tab === 'register' ? 'Kayıt Ol' : tab === 'forgot' ? 'Şifremi Unuttum' : tab === 'twofa' ? 'İki Adımlı Doğrulama' : 'Yeni Şifre Belirle'}
            </h2>
            </>
          ) : (
            <>
              <h2 id="auth-modal-title" className="mb-1">
                <Logo linked={false} size="md" />
              </h2>
              <p className="text-sm text-gray-400 mb-6">
                {tab === 'login' ? 'Hesabınıza giriş yapın' : tab === 'register' ? 'Yeni hesap oluşturun' : tab === 'forgot' ? 'Sıfırlama bağlantısı isteyin' : tab === 'twofa' ? 'İki adımlı doğrulama' : 'Yeni şifrenizi belirleyin'}
              </p>
            </>
          )}

          {(tab === 'login' || tab === 'register') && <div className="auth-tabs flex bg-black/40 rounded-xl p-1 mb-6 border border-white/5">
            <button onClick={() => setTab('login')} className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-colors ${tab === 'login' ? 'gold-btn' : 'text-gray-300 hover:text-white'}`}>Giriş Yap</button>
            {registrationOpen && <button onClick={() => setTab('register')} className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-colors ${tab === 'register' ? 'gold-btn' : 'text-gray-300 hover:text-white'}`}>Kayıt Ol</button>}
          </div>}

          {tab === 'forgot' ? (
            <form onSubmit={submitForgot} className="space-y-3">
              <div className="relative">
                <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--pusu-gold)]" />
                <input required type="email" className="pusu-input pl-10" placeholder="E-Posta" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} />
              </div>
              <button type="submit" className="gold-btn w-full py-3 rounded-xl">Sıfırlama Bağlantısı Gönder</button>
              <button type="button" className="gold-outline-btn w-full py-3 rounded-xl" onClick={() => setTab('login')}>Girişe Dön</button>
            </form>
          ) : tab === 'twofa' ? (
            <form onSubmit={submitTwoFactor} className="space-y-3">
              <div className="flex items-start gap-3 rounded-xl p-3" style={{ background: 'rgba(var(--pusu-gold-rgb),0.08)', border: '1px solid rgba(var(--pusu-gold-rgb),0.25)' }}>
                <ShieldCheck size={18} className="text-[var(--pusu-gold)] mt-0.5 shrink-0" />
                <p className="text-xs text-gray-300">
                  {twoFactor?.deliveryChannel === 'totp' ? (
                    <>
                      <span className="font-semibold text-white">Google Authenticator</span> uygulamanızdaki güncel {twoFactor?.codeLength || 6} haneli kodu girin. Kod yaklaşık 30 saniyede bir yenilenir.
                    </>
                  ) : (
                    <>
                      <span className="font-semibold text-white">{twoFactor?.deliveryTarget}</span> hedefine {twoFactor?.codeLength || 6} haneli bir doğrulama kodu gönderdik. Kod 10 dakika geçerlidir.
                    </>
                  )}
                </p>
              </div>
              <input
                required
                autoFocus
                inputMode="numeric"
                autoComplete="one-time-code"
                minLength={twoFactor?.codeLength || 6}
                maxLength={twoFactor?.codeLength || 6}
                pattern="[0-9]{6}"
                className="pusu-input text-center text-2xl tracking-[0.5em] font-bold"
                placeholder="000000"
                value={twoFactorCode}
                onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, ''))}
              />
              <button type="submit" disabled={twoFactorBusy || twoFactorCode.length !== (twoFactor?.codeLength || 6)} className="gold-btn w-full py-3 rounded-xl disabled:opacity-50">
                {twoFactor?.purpose === 'register' ? 'Doğrula ve Kaydı Tamamla' : 'Doğrula ve Giriş Yap'}
              </button>
              <div className="flex items-center justify-between text-xs">
                <button type="button" onClick={cancelTwoFactor} className="text-gray-400 hover:text-white">Girişe dön</button>
                {twoFactor?.deliveryChannel !== 'totp' && (
                  <button type="button" disabled={twoFactorBusy} onClick={resendCode} className="text-[var(--pusu-gold)] hover:underline disabled:opacity-50">Kodu tekrar gönder</button>
                )}
              </div>
            </form>
          ) : tab === 'reset' ? (
            <form onSubmit={submitReset} className="space-y-3">
              <div className="relative">
                <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--pusu-gold)]" />
                <input required minLength={RESET_PASSWORD_MIN_LENGTH} type="password" className="pusu-input pl-10" placeholder="Yeni şifre" value={resetPassword} onChange={(e) => setResetPassword(e.target.value)} aria-describedby="reset-password-hint" />
              </div>
              <p
                id="reset-password-hint"
                aria-live="polite"
                className={`text-[11px] leading-relaxed -mt-1 ${passwordTooShort(resetPassword, RESET_PASSWORD_MIN_LENGTH) ? 'text-red-400' : 'text-gray-500'}`}
              >
                {passwordTooShort(resetPassword, RESET_PASSWORD_MIN_LENGTH)
                  ? `Şifreniz çok kısa: ${resetPassword.length}/${RESET_PASSWORD_MIN_LENGTH} karakter.`
                  : `Şifreniz en az ${RESET_PASSWORD_MIN_LENGTH} karakter olmalıdır.`}
              </p>
              <div className="relative">
                <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--pusu-gold)]" />
                <input required minLength={RESET_PASSWORD_MIN_LENGTH} type="password" className="pusu-input pl-10" placeholder="Yeni şifre tekrar" value={resetConfirm} onChange={(e) => setResetConfirm(e.target.value)} />
              </div>
              <button type="submit" className="gold-btn w-full py-3 rounded-xl">Şifremi Güncelle</button>
            </form>
          ) : tab === 'login' ? (
            <form onSubmit={submitLogin} className="space-y-3">
              <div className="relative">
                {fieldLabel('Kullanıcı Adı veya E-posta')}
                <User size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--pusu-gold)]" />
                <input className="pusu-input pl-10" placeholder={herkul ? 'Kullanıcı adınız' : 'Kullanıcı Adı'} value={loginForm.username} onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })} />
              </div>
              <div className="relative">
                {fieldLabel('Şifre')}
                <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--pusu-gold)]" />
                <input type={showPass ? 'text' : 'password'} className="pusu-input pl-10 pr-10" placeholder="Şifre" value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} />
                <button type="button" onClick={() => setShowPass((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white">{showPass ? <EyeOff size={16} /> : <Eye size={16} />}</button>
              </div>
              <div className={`flex items-center text-xs ${herkul ? 'justify-end' : 'justify-between'}`}>
                {!herkul && <label className="flex items-center gap-2 text-gray-400"><input type="checkbox" className="accent-[var(--pusu-gold)]" /> Beni hatırla</label>}
                <button type="button" onClick={() => setTab('forgot')} className="text-[var(--pusu-gold)] hover:underline">{herkul ? 'Şifremi Unuttum?' : 'Şifremi Unuttum'}</button>
              </div>
              <button type="submit" className="gold-btn w-full py-3 rounded-xl">Giriş Yap</button>
              {registrationOpen && <div className="text-center text-xs text-gray-400 pt-2">{herkul ? 'Hesabın yok mu? ' : 'Henüz üyeliğiniz yok mu? '}<button type="button" className="text-[var(--pusu-gold)] font-semibold" onClick={() => setTab('register')}>{herkul ? 'Kayıt Ol' : 'Hemen Kayıt Olun'}</button></div>}
            </form>
          ) : (
            <form onSubmit={submitRegister} className="space-y-3">
              <div className="relative">
                {fieldLabel('Kullanıcı Adı')}
                <User size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--pusu-gold)]" />
                <input required minLength={3} className="pusu-input pl-10" placeholder={herkul ? 'kullanıcıadı' : 'Kullanıcı Adı'} value={regForm.username} onChange={(e) => setRegForm({ ...regForm, username: e.target.value })} />
              </div>
              <div className="relative">
                {fieldLabel('Şifre')}
                <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--pusu-gold)]" />
                <input required minLength={REGISTER_PASSWORD_MIN_LENGTH} type={showPass ? 'text' : 'password'} className="pusu-input pl-10 pr-10" placeholder={herkul ? `En az ${REGISTER_PASSWORD_MIN_LENGTH} karakter` : `Şifre (en az ${REGISTER_PASSWORD_MIN_LENGTH} karakter)`} value={regForm.password} onChange={(e) => setRegForm({ ...regForm, password: e.target.value })} aria-describedby="register-password-hint" />
                <button type="button" onClick={() => setShowPass((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">{showPass ? <EyeOff size={16} /> : <Eye size={16} />}</button>
              </div>
              <p
                id="register-password-hint"
                aria-live="polite"
                className={`text-[11px] leading-relaxed -mt-1 ${passwordTooShort(regForm.password, REGISTER_PASSWORD_MIN_LENGTH) ? 'text-red-400' : 'text-gray-500'}`}
              >
                {passwordTooShort(regForm.password, REGISTER_PASSWORD_MIN_LENGTH)
                  ? `Şifreniz çok kısa: ${regForm.password.length}/${REGISTER_PASSWORD_MIN_LENGTH} karakter.`
                  : `Şifreniz en az ${REGISTER_PASSWORD_MIN_LENGTH} karakter olmalıdır.`}
              </p>
              <div className="relative">
                {fieldLabel('E-posta')}
                <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--pusu-gold)]" />
                <input required type="email" className="pusu-input pl-10" placeholder={herkul ? 'ornek@email.com' : 'E-Posta'} value={regForm.email} onChange={(e) => setRegForm({ ...regForm, email: e.target.value })} />
              </div>
              <div className="relative">
                {fieldLabel(`Telefon${registrationOtpEnabled ? ' *' : ''}`)}
                <Phone size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--pusu-gold)]" />
                <input required={registrationOtpEnabled} inputMode="tel" autoComplete="tel" className="pusu-input pl-10" placeholder={herkul ? '(555) 000 00 00' : `Telefon (5xxxxxxxxx)${registrationOtpEnabled ? ' *' : ''}`} value={regForm.phone} onChange={(e) => setRegForm({ ...regForm, phone: e.target.value })} />
              </div>

              {identityAtSignup ? (
                <>
                <div className="flex items-center gap-2 pt-2 pb-1">
                  <ShieldCheck size={14} className="text-[var(--pusu-gold)]" />
                  <span className="text-xs font-semibold text-gray-300">Kimlik Bilgileri</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="relative">
                    {fieldLabel('Ad')}
                    <UserRound size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--pusu-gold)]" />
                    <input required maxLength={128} className="pusu-input pl-10" placeholder={herkul ? 'Adınız' : 'Ad'} autoComplete="given-name" value={regForm.first_name} onChange={(e) => setRegForm({ ...regForm, first_name: e.target.value })} />
                  </div>
                  <div className="relative">
                    {fieldLabel('Soyad')}
                    <UserRound size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--pusu-gold)]" />
                    <input required maxLength={128} className="pusu-input pl-10" placeholder={herkul ? 'Soyadınız' : 'Soyad'} autoComplete="family-name" value={regForm.last_name} onChange={(e) => setRegForm({ ...regForm, last_name: e.target.value })} />
                  </div>
                </div>
                <div className="relative">
                  {fieldLabel('TC Kimlik No')}
                  <IdCard size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--pusu-gold)]" />
                  <input
                    required
                    inputMode="numeric"
                    pattern="\d{11}"
                    minLength={11}
                    maxLength={11}
                    autoComplete="off"
                    className="pusu-input pl-10"
                    placeholder={herkul ? '11 haneli kimlik no' : 'T.C. Kimlik No (11 hane)'}
                    value={regForm.tc_no}
                    onChange={(e) => setRegForm({ ...regForm, tc_no: e.target.value.replace(/\D/g, '').slice(0, 11) })}
                  />
                </div>
                <div className="relative">
                  {fieldLabel('Doğum Tarihi')}
                  <CalendarDays size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--pusu-gold)] pointer-events-none" />
                  <input
                    required
                    type="date"
                    max={maxBirthDate}
                    className="pusu-input pl-10"
                    aria-label="Doğum tarihi"
                    value={regForm.birth_date}
                    onChange={(e) => setRegForm({ ...regForm, birth_date: e.target.value })}
                  />
                </div>
                <p className="text-[11px] text-gray-500 leading-relaxed">
                  Kimlik bilgileriniz kimliğinizde yazdığı biçimde olmalıdır. Onaylandıktan sonra değiştirilemez ve para yatırma/çekme işlemleri için kullanılır.
                </p>
                </>
              ) : (
                <p className="text-[11px] text-gray-500 leading-relaxed">
                  Kimlik bilgileriniz kayıt sırasında sorulmaz. Para çekebilmek
                  için üyeliğiniz açıldıktan sonra Hesabım &rarr; KYC Doğrulama
                  sayfasından kimlik bilgilerinizi onaya gönderin.
                </p>
              )}

              <label className="flex items-start gap-2 text-xs text-gray-400 pt-1">
                <input type="checkbox" className="mt-0.5 accent-[var(--pusu-gold)]" checked={regForm.accept} onChange={(e) => setRegForm({ ...regForm, accept: e.target.checked })} />
                <span>18 yaşından büyük olduğumu, <span className="text-[var(--pusu-gold)]">Kullanıcı Sözleşmesi</span> ve <span className="text-[var(--pusu-gold)]">Gizlilik Politikası</span>’nı kabul ediyorum.</span>
              </label>
              <button type="submit" className="gold-btn w-full py-3 rounded-xl">Kayıt Ol</button>
              <div className="text-center text-xs text-gray-400 pt-2">{herkul ? 'Hesabın var mı? ' : 'Zaten üye misiniz? '}<button type="button" className="text-[var(--pusu-gold)] font-semibold" onClick={() => setTab('login')}>{herkul ? 'Giriş Yap' : 'Giriş Yapın'}</button></div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
